import { useCallback, useEffect, useState } from "react";
import { analytics } from "./analytics";
import { captureException } from "./sentry";
import { supabase } from "./supabase";

/** Max length accepted by the beta_waitlist.use_case CHECK constraint */
export const USE_CASE_MAX_LENGTH = 1000;

/** Max length accepted by the beta_feedback.body CHECK constraint */
export const FEEDBACK_BODY_MAX_LENGTH = 4000;

/** Max length accepted by the beta_feedback.page_context CHECK constraint */
export const FEEDBACK_PAGE_CONTEXT_MAX_LENGTH = 300;

export type BetaWaitlistStatus = "none" | "pending" | "granted" | "declined";

export type BetaFeedbackCategory = "bug" | "idea" | "friction" | "other";

export const BETA_FEEDBACK_CATEGORIES: { value: BetaFeedbackCategory; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "friction", label: "Friction" },
  { value: "other", label: "Other" },
];

export interface BetaAccessStatus {
  /** True when the has_beta_access(user_id) predicate passes server-side */
  isBeta: boolean;
  waitlistStatus: BetaWaitlistStatus;
  requestedAt: string | null;
}

function isDuplicateKeyError(error: unknown): boolean {
  return !!error && typeof error === "object" && (error as { code?: unknown }).code === "23505";
}

/**
 * Read the current user's beta status: the server predicate plus their
 * waitlist row (if any). Signed-out users are never beta.
 */
export async function getBetaAccessStatus(): Promise<BetaAccessStatus> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return { isBeta: false, waitlistStatus: "none", requestedAt: null };
  }

  const [predicate, row] = await Promise.all([
    supabase.rpc("has_beta_access", { p_user_id: user.id }),
    supabase
      .from("beta_waitlist")
      .select("status, requested_at")
      .eq("user_id", user.id)
      .maybeSingle(),
  ]);

  if (predicate.error) throw predicate.error;
  if (row.error) throw row.error;

  const waitlistStatus = (row.data?.status as BetaWaitlistStatus | undefined) ?? "none";

  return {
    isBeta: predicate.data === true,
    waitlistStatus,
    requestedAt: row.data?.requested_at ?? null,
  };
}

/**
 * Join the beta waitlist for the current user. Inserting is idempotent from
 * the caller's perspective: a duplicate row resolves as alreadyOnList.
 */
export async function joinBetaWaitlist(
  useCase?: string,
): Promise<{ joined: boolean; alreadyOnList: boolean }> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in to join the beta waitlist");

  const trimmed = useCase?.trim().slice(0, USE_CASE_MAX_LENGTH) ?? "";

  const { error } = await supabase.from("beta_waitlist").insert({
    user_id: user.id,
    use_case: trimmed.length > 0 ? trimmed : null,
  });

  if (error) {
    if (isDuplicateKeyError(error)) {
      return { joined: false, alreadyOnList: true };
    }
    throw error;
  }

  analytics.beta.waitlistJoined({ has_use_case: trimmed.length > 0 });
  return { joined: true, alreadyOnList: false };
}

export interface BetaFeedbackInput {
  category: BetaFeedbackCategory;
  body: string;
  pageContext?: string;
}

/** Insert a beta_feedback row for the current (beta) user. */
export async function submitBetaFeedback(input: BetaFeedbackInput): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in to send feedback");

  const body = input.body.trim().slice(0, FEEDBACK_BODY_MAX_LENGTH);
  if (body.length === 0) throw new Error("Feedback can't be empty");

  const pageContext = input.pageContext?.trim().slice(0, FEEDBACK_PAGE_CONTEXT_MAX_LENGTH);

  const { error } = await supabase.from("beta_feedback").insert({
    user_id: user.id,
    category: input.category,
    body,
    page_context: pageContext && pageContext.length > 0 ? pageContext : null,
  });

  if (error) throw error;

  analytics.beta.feedbackSubmitted({
    category: input.category,
    page_context: pageContext || undefined,
  });
}

/**
 * Hook: beta access + waitlist state for the current user.
 * Mirrors the useTierCredits pattern (fetch on mount, expose refresh).
 */
export function useBetaAccess() {
  const [status, setStatus] = useState<BetaAccessStatus | null>(null);
  const [checkFailed, setCheckFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setStatus(await getBetaAccessStatus());
      setCheckFailed(false);
    } catch (err) {
      // Transient failure (network flap, RPC hiccup) — NOT "not beta".
      // Client gates should fail open on this; the byok-proxy enforces
      // beta access server-side regardless (mirrors useTierCredits).
      setCheckFailed(true);
      void captureException(err, { tags: { module: "beta-access" } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const join = useCallback(
    async (useCase?: string) => {
      const result = await joinBetaWaitlist(useCase);
      await refresh();
      return result;
    },
    [refresh],
  );

  return {
    /** True when has_beta_access(user_id) passes server-side */
    isBeta: status?.isBeta ?? false,
    /**
     * True when the beta check itself errored (network/RPC failure) —
     * distinct from "definitely not beta". Gates should treat this as
     * beta-for-now and let the server enforce.
     */
    checkFailed,
    /** True when the user has a pending waitlist request */
    isPending: status?.waitlistStatus === "pending",
    waitlistStatus: status?.waitlistStatus ?? "none",
    join,
    loading,
    refresh,
  };
}
